
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SecurityLightingInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Security Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional security lighting installation in Fresno, CA. Motion sensor lights, floodlights, and perimeter lighting to protect your property.',
    url: `${siteUrl}/services/lighting-outdoor/security-lighting-installation`,
  };

  const features = [
    {
      icon: 'ri-shield-star-line',
      title: 'Motion Sensor Lights',
      description: 'Automatic activation when movement is detected around your property',
    },
    {
      icon: 'ri-flashlight-fill',
      title: 'LED Floodlights',
      description: 'Bright, energy-efficient lighting for driveways and entryways',
    },
    {
      icon: 'ri-sun-line',
      title: 'Dusk-to-Dawn Sensors',
      description: 'Automatic lighting that turns on at sunset and off at sunrise',
    },
    {
      icon: 'ri-camera-line',
      title: 'Camera Integration',
      description: 'Coordinate lighting with security camera systems',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Smart Controls',
      description: 'Control and schedule security lights from your phone',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Strategic Placement',
      description: 'Expert positioning to eliminate dark spots and deter intruders',
    },
  ];

  return (
    <>
      <SEOHead
        title="Security Lighting Installation Fresno CA | Motion Sensor Lights"
        description="Professional security lighting installation in Fresno, CA. Motion sensor lights, LED floodlights, and perimeter lighting to protect your home. Licensed electricians."
        canonical="/services/lighting-outdoor/security-lighting-installation"
        keywords="security lighting Fresno, motion sensor lights, floodlights, perimeter lighting"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Security Lighting Installation — Fresno, CA"
        heroSubtitle="Protect your Fresno property with professional security lighting systems"
        heroImage="https://public.readdy.ai/ai/img_res/31957f2cd07be375ba672409ba2e123d.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20modern%20LED%20motion%20sensor%20security%20light%20fixture%20mounted%20on%20house%20exterior%20wall%20with%20bright%20white%20light%20activated%20California%20home%20no%20people%20present&width=700&height=600&seq=security-light-fresno-006&orientation=portrait"
        fresnoContent="Enhance your Fresno property's security with professionally installed motion sensor lights and floodlights. From Tower District homes to Woodward Park estates, we design comprehensive security lighting systems that deter intruders and illuminate dark areas around your property. Our licensed electricians install energy-efficient LED fixtures with motion sensors, dusk-to-dawn controls, and smart home integration. Strategic placement ensures complete coverage of entryways, driveways, backyards, and perimeter areas."
        features={features}
        faqs={[
          {
            question: 'How effective is security lighting in preventing break-ins in Fresno?',
            answer:
              'Security lighting is highly effective in Fresno. Studies show well-lit properties are 3x less likely to be targeted by burglars. Motion-activated lights startle intruders and alert homeowners to activity around the property. Combined with visible placement near entry points, security lighting creates a strong deterrent. Many Fresno homeowners integrate security lights with camera systems for comprehensive protection. We design lighting systems that eliminate dark hiding spots and provide clear visibility around your entire property perimeter.',
          },
          {
            question: 'What type of security lights work best in Fresno?',
            answer:
              "For Fresno properties, we recommend LED motion sensor floodlights for driveways and entryways, dusk-to-dawn LED fixtures for continuous perimeter lighting, and motion-activated wall lights near doors and windows. LED fixtures are ideal for Fresno's climate—they handle extreme heat, use 75% less energy, and last 25+ years. Motion sensors should have adjustable sensitivity and range (20–40 feet typical). We also install smart security lights that send alerts to your phone and integrate with home automation systems.",
          },
          {
            question: 'How much does security lighting installation cost in Fresno?',
            answer:
              'Security lighting installation in Fresno typically costs $300–$800 per fixture depending on type and complexity. Basic motion sensor lights start around $250–$400 per fixture, while high-output LED floodlights with smart controls range from $500–$800. Whole-property security lighting systems with 4–6 fixtures typically cost $2,000–$4,000. We provide free security assessments and detailed quotes for all Fresno properties. Most homeowners see immediate ROI through reduced insurance premiums and increased property value.',
          },
          {
            question: 'Where should security lights be installed on Fresno homes?',
            answer:
              'For optimal security in Fresno, we install lights at all entry points (front door, back door, side doors, garage), along driveways and walkways, in backyards and side yards, near windows on ground level, and at property corners for perimeter coverage. Motion sensors should cover approach paths before intruders reach doors or windows. We conduct on-site assessments to identify vulnerable areas and design lighting layouts that eliminate all dark spots around your Fresno property.',
          },
          {
            question: 'Can security lights be integrated with smart home systems in Fresno?',
            answer:
              'Yes! We install smart security lighting throughout Fresno that integrates with Alexa, Google Home, Ring, and other smart home platforms. Smart security lights allow you to control fixtures remotely, receive motion alerts on your phone, create lighting schedules, and coordinate with security cameras and alarm systems. Many Fresno homeowners use geofencing to automatically turn lights on when they arrive home. We handle all wiring, fixture installation, and smart system configuration.',
          },
          {
            question: 'Do security lights require permits in Fresno?',
            answer:
              'Most security lighting installations in Fresno require electrical permits, especially when adding new circuits or modifying existing wiring. Simple fixture replacements on existing circuits may not require permits. We handle all permit applications and inspections with the City of Fresno, ensuring full compliance with California electrical codes. Our licensed C-10 electricians ensure all installations meet Fresno building standards. Proper permitting protects your home value and ensures insurance coverage.',
          },
        ]}
      />
    </>
  );
}
